import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { getOfferList } from 'cityads-v3/actions/offers';
import { amplitudeSendEvent } from 'cityads-v3/utils/counterUtils';

export function useOfferInfoSender(offerId) {
  const dispatch = useDispatch();

  const listedOfferRequestParams = useMemo(() => {
    const params = { ids: [offerId] };

    return {
      raw: params,
      stringified: JSON.stringify(params),
    }
  }, [offerId]);

  const listedOffer = useSelector(state => state.offers.offerListById[offerId]);
  const isLoadingListedOffer = useSelector(state => state.offers.isLoadingOfferList[listedOfferRequestParams.stringified]);

  useEffect(() => {
    if (offerId && !listedOffer && !isLoadingListedOffer) {
      dispatch(getOfferList(listedOfferRequestParams.raw));
    }
  }, [offerId, listedOfferRequestParams, listedOffer, isLoadingListedOffer, dispatch]);

  return useCallback((eventId, eventName) => {
    if (offerId && listedOffer) {
      amplitudeSendEvent('makeActions', eventId, 'offers', eventName, {
        offer_id: offerId,

        payout_sum: listedOffer.offer_targets.map(({ payment, payment_max, is_percent }) => {
          const suffix = is_percent ? '%' : ` ${listedOffer.offer_currency.name}`;

          if (payment_max && payment_max > payment) {
            return `${payment}${suffix} - ${payment_max}${suffix}`;
          } else {
            return `${payment}${suffix}`;
          }
        }),

        goal_name: listedOffer.offer_targets.map(target => target.name),

        traffic_options: [
          ...listedOffer.traffic_types.filter(trafficType => trafficType.allowed).map(trafficType => trafficType.name),
          ...listedOffer.additional_traffic_types.filter(trafficType => trafficType.allowed).map(trafficType => trafficType.name),
        ],

        tools         : listedOffer.tools.map(tool => tool.name),
        offer_category: listedOffer.category.name,
        offer_type    : listedOffer.type.name,
        approval_time : listedOffer.approval_time,
        payout_time   : listedOffer.payment_time,
      })
    }
  }, [offerId, listedOffer]);
}

export function useCouponInfoSender(couponId) {
  const coupon = useSelector(state => state.offers.couponsById[couponId]);

  const regionsById = useSelector(state => state.offers.regionsById);
  const promoActionTypesById = useSelector(state => state.offers.promoActionTypesById);
  const languagesById = useSelector(state => state.language.sadukaLanguagesById);
  const promoCouponStatusesById = useSelector(state => state.offers.promoCouponStatusesById);

  return useCallback((eventId, eventName) => {
    if (couponId && coupon && regionsById && promoActionTypesById && languagesById && promoCouponStatusesById) {
      amplitudeSendEvent('makeActions', eventId, 'goods', eventName, {
        coupon_region  : coupon.geos.map(geoId => regionsById[geoId]?.name).filter(_ => !!_),
        coupon_type    : promoActionTypesById[coupon.promo_action_type]?.name ?? '',
        coupon_language: languagesById[coupon.language_id]?.name ?? '',
        coupon_status  : coupon.status.map(statusId => promoCouponStatusesById[statusId]?.name).filter(_ => !!_),
        offer_id       : Number(coupon.offer),
      })
    }
  }, [couponId, coupon, regionsById, promoActionTypesById, languagesById, promoCouponStatusesById]);
}